import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { useSetReturnMode } from "@babackapp/return-widget-components/hooks/use-set-return-mode";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useReturnReasons } from "@babackapp/return-widget-components/hooks/use-return-reasons";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useWidgetParams } from "./use-widget-params";
import { useAuth } from "@babackapp/auth-client/hooks";
import type { ReturnMode } from "@babackapp/return-widget-components/types";
import {
  filterStoresByCountry,
  sortStoresByProximity,
} from "../utils/store-selection";

const returnModeSchema = z
  .object({
    returnMode: z.string(),
    pickupDate: z.string().optional(),
    storeId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.returnMode === "carrier" && !data.pickupDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["pickupDate"],
        message: "required",
      });
    }
    if (data.returnMode === "store" && !data.storeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["storeId"],
        message: "required",
      });
    }
  });

type ReturnModeFormValues = {
  returnMode: ReturnMode;
  pickupDate?: string;
  storeId?: string;
};

type LineItemWithTags = { line_item_id?: string; product?: { tags?: string[] } | null };

export function useReturnModeForm() {
  const navigate = useNavigate();
  const { shopId, orderId } = useWidgetParams();
  const { data: shop } = usePublicShop(shopId);
  const { data: order } = usePublicOrder(orderId);
  const draft = useDraftReturn(orderId);
  const setReturnMode = useSetReturnMode(orderId);
  const { user } = useAuth();
  const isMerchant = !!user;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultPickupDate = tomorrow.toISOString().split("T")[0];

  const form = useForm<ReturnModeFormValues>({
    resolver: zodResolver(returnModeSchema),
    defaultValues: {
      returnMode: "carrier",
      pickupDate: defaultPickupDate,
      storeId: undefined,
    },
  });

  const shippingCountry = order?.shippingAddress?.country_code;
  const shippingZip = order?.shippingAddress?.zip;

  const allStores = (shop.returnToStore?.stores ?? []).filter(
    (s) => s.selectableByUser,
  );
  const countryFilteredStores = filterStoresByCountry(
    allStores,
    shippingCountry,
    isMerchant,
  );
  const selectableStores = sortStoresByProximity(countryFilteredStores, shippingZip);

  const lineItems = (order?.lineItems ?? []) as LineItemWithTags[];
  const draftLineItemIds = draft?.lineItems.map((li) => li.lineItemId) ?? [];
  const hasNoReturnToStoreTag = draftLineItemIds.some((id) => {
    const li = lineItems.find((l) => l.line_item_id === id);
    return li?.product?.tags?.includes("BB_NO_RETURN_TO_STORE") ?? false;
  });

  const hasStoreReturns =
    shop.returnToStore?.activated === true && !hasNoReturnToStoreTag;

  const reasons = useReturnReasons(shopId);
  const retractationReasonIds = new Set(
    reasons.filter((r) => r.isRetractation).map((r) => r.id),
  );
  const hasRetractationItem = (draft?.lineItems ?? []).some(
    (li) => li.reasonId != null && retractationReasonIds.has(li.reasonId),
  );
  const shopPrefsAny = (shop as { preferences?: { retractationEnabled?: boolean } }).preferences;
  const retractationEnabled = shopPrefsAny?.retractationEnabled === true;

  const availableReturnModes: ReturnMode[] = ["carrier"];
  if (hasStoreReturns) availableReturnModes.push("store");
  if (retractationEnabled && hasRetractationItem) availableReturnModes.push("retractation");

  const hasExchangeItem = (draft?.lineItems ?? []).some(
    (li) => li.resolution === "exchange",
  );
  const finalReturnModes = availableReturnModes.filter(
    (mode) => !(mode === "store" && hasExchangeItem && !isMerchant),
  );

  const onSubmit = form.handleSubmit(async (values) => {
    setReturnMode(
      values.returnMode as ReturnMode,
      values.returnMode === "store" ? (values.storeId ?? null) : null,
      values.returnMode === "carrier" ? (values.pickupDate ?? null) : null,
    );
    navigate("../address");
  });

  return {
    form,
    onSubmit,
    availableReturnModes: finalReturnModes,
    selectableStores,
    hasStoreReturns,
  };
}
